'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { isValidElement, PureComponent } from 'react';
import classNames from 'classnames';
import devLogger from 'react-utils/devLogger';
import Controllable from '../decorators/Controllable';
import { defaultCounters, defaultLabeler, defaultOptions } from './CounterFunctions';
import { defaultRenderers } from './RenderFunctions';

const supportsCharacterCount = renderedInput => {
  if (! /*#__PURE__*/isValidElement(renderedInput)) return false;
  if (!renderedInput.type.propTypes) return false;
  return !!renderedInput.type.propTypes.characterCount;
};

const getCounterFunction = counter => {
  if (typeof counter === 'string') {
    return defaultCounters[counter];
  }

  return counter;
};

class UICharacterCounter extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      count: null,
      label: null,
      prevValue: undefined
    };
    const {
      counter,
      label
    } = this.props;

    if (process.env.NODE_ENV !== 'production') {
      if (typeof counter === 'function' && label === defaultLabeler) {
        devLogger.warn({
          key: 'UICharacterCounter missing label generator',
          message: 'UICharacterCounter: if you provide a `counter` function you ' + 'must also provide a corresponding `label` function. The default ' + 'label generator only works with the standard "up" or "down" counters.'
        });
      }
    }
  }

  static getDerivedStateFromProps(props, state) {
    const {
      counter,
      label,
      max,
      maxLength,
      value
    } = props;
    const nextCount = getCounterFunction(counter)(state.prevVal, value, {
      max,
      maxLength
    });
    const nextLabel = typeof label === 'function' ? label({
      count: nextCount
    }, counter) : label;
    return {
      count: nextCount,
      label: nextLabel,
      prevValue: props.value
    };
  }

  render() {
    const _this$props = this.props,
          {
      className,
      errorCondition,
      labelClassName,
      renderInput,
      value
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["className", "counter", "errorCondition", "labelClassName", "max", "renderInput", "value"]);

    const {
      count,
      label
    } = this.state;
    const renderedInput = typeof renderInput === 'string' ? defaultRenderers[renderInput](Object.assign({}, rest, {
      error: errorCondition(count, value),
      label,
      value
    })) : renderInput(Object.assign({}, rest, {
      count,
      label,
      value
    }));
    const renderedLabel = supportsCharacterCount(renderedInput) ? null : /*#__PURE__*/_jsx("label", {
      className: classNames('private-char-count__label', labelClassName),
      "aria-live": "polite",
      children: label
    });
    return /*#__PURE__*/_jsxs("div", {
      className: classNames('private-char-count', className, count < 0 && 'private-char-count--negative'),
      children: [renderedInput, renderedLabel]
    });
  }

}

UICharacterCounter.propTypes = {
  /**
   * Defines the behavior of the counter that updates as a user types. The built-in counters go either `“up"` for counting toward the max value, or `"down"` for counting toward 0. Custom `counter` functions can be invoked with 3 arguments: the previous value, current value, and an object containing the `max` and `maxLength` props.
   **/
  counter: PropTypes.oneOfType([PropTypes.oneOf(Object.keys(defaultCounters)), PropTypes.func]).isRequired,

  /**
   * Sets the value of the text field on initial render. Use in combination with the `onChange` prop to respond to user changes.
   **/
  defaultValue: PropTypes.string,

  /**
   * Function that when it returns true, shows an error affordance. The default function returns true if the count is less than 0.
   **/
  errorCondition: PropTypes.func,

  /**
   * Customizes how the character count is rendered. If passed a node, the label will be static and will not update on type.
   **/
  label: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,

  /**
   * Sets the classname on the character count
   **/
  labelClassName: PropTypes.string,

  /**
   * Sets the max number of characters that is valid for the text field. Shows an error affordance if the count is higher than the max.
   **/
  max: PropTypes.number,

  /**
   * Enforces the number of characters that can be typed into the text field. Differs from the max prop because it prevents typing and does not show an error when the `maxLength` is hit.
   **/
  maxLength: PropTypes.number,

  /**
   * Event handler function that fires on every input blur event
   **/
  onBlur: PropTypes.func,

  /**
   * Event handler function that fires on every input change event
   **/
  onChange: PropTypes.func,

  /**
   * Determines what type of component the character counter is wrapped around. We recommend rendering a `UIFormControl` in a function here to add a label and make sure it is accessible.
   **/
  renderInput: PropTypes.oneOfType([PropTypes.oneOf(Object.keys(defaultRenderers)), PropTypes.func]).isRequired,

  /**
   * The controlled content of the rendered input field. Use in combination with the `onChange` event handler to respond to user input changes
   **/
  value: PropTypes.string
};
UICharacterCounter.defaultProps = Object.assign({}, defaultOptions, {
  counter: 'up',
  errorCondition: count => count < 0,
  label: defaultLabeler,
  renderInput: 'textarea',
  value: ''
});
UICharacterCounter.displayName = 'UICharacterCounter';
export default Controllable(UICharacterCounter);